import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The CreateAccountInput used by the constructor of CreateAccount
 */
export interface CreateAccountInput {
  /**
   * first name on the account.
   */
  firstName?: string;
  /**
   * last name on the account.
   */
  lastName?: string;
  /**
   * email on the account.
   */
  email?: string;
  /**
   * phone number on the account, should start with +{country code}
   */
  phone?: string;
  /**
   * any additional metadata attributes required by the account.
   */
  metadata?: {
    [key: string]: string;
  };
  /**
   * ledgerType for the account, default is 'hedera' if not provided.
   */
  ledgerType?: string;
}

/**
 * The CreateAccount response expected from the execution of the object within BambuClient execute.
 */
export interface CreateAccountResponse {
  /**
   * the message response from the request
   */
  message: string;
  /**
   * any error messages that were returned by the request.
   */
  error?: any;
  /**
   * If the account already exists, the accountId is returned.
   */
  accountId?: string;
  /**
   * If the account already exists, the ledgerId is returned.
   */
  ledgerId?: string;
}

/**
 * The create account api is an asyncronous request to BambuMeta to issue the provided user an account.  This request
 * will not guarentee an account is created.  But checks if there is already an existing account.  If so, the account id is returned.  If no account already exists,
 * the request will be added to the processor for consumption and a success message should be returned.
 */
export class CreateAccount extends Executable<CreateAccountResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'post';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the CreateAccountResponse
   * @returns CreateAccountResponse
   */
  public getResponse(apiResponse: any): CreateAccountResponse {
    return apiResponse;
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    return `${tenantId}/create-account?ledger_type=${this._ledgerType}`;
  }

  private _firstName?: string;

  private _lastName?: string;

  private _email?: string;

  private _phone?: string;

  private _metadata?: any;

  private _ledgerType: string = 'hedera';

  constructor(input?: CreateAccountInput) {
    super();
    if (input) {
      this._email = input.email;
      this._firstName = input.firstName;
      this._lastName = input.lastName;
      this._phone = input.phone;
      this._metadata = input.metadata;
    }
    if (input?.ledgerType) {
      this._ledgerType = input.ledgerType;
    }
  }

  /**
   * Sets the first name on the account.
   * @param value <string> first name
   * @returns <CreateAccount>
   */
  public setFirstName(value: string): CreateAccount {
    this._firstName = value;
    return this;
  }

  /**
   * Sets the last name on the account.
   * @param value <string> last name
   * @returns <CreateAccount>
   */
  public setLastName(value: string): CreateAccount {
    this._lastName = value;
    return this;
  }

  /**
   * Sets the email address on the account.
   * @param value <string> email
   * @returns <CreateAccount>
   */
  public setEmail(value: string): CreateAccount {
    this._email = value;
    return this;
  }

  /**
   * Sets the phone number on the account.
   * @param value <string> phone number, should start with +{country code}
   * @returns <CreateAccount>
   */
  public setPhone(value: string): CreateAccount {
    this._phone = value;
    return this;
  }

  /**
   * Sets the ledger type on the account.
   * @param value <string> ledger type
   * @returns <CreateAccount>
   */
  public setLedgerType(value: string): CreateAccount {
    this._ledgerType = value;
    return this;
  }

  /**
   * Sets any additional metadata to be stored against the account record
   * @param key the metadata attribute key
   * @param value the metadata attribute value
   * @returns <CreateAccount>
   */
  public setMetaData(key: string, value: string): CreateAccount {
    if (!this._metadata) {
      this._metadata = {};
    }
    this._metadata[key] = value;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    const input: any = {};
    if (this._email) {
      input.email = this._email;
    }
    if (this._phone) {
      input.phone = this._phone;
    }
    if (this._firstName) {
      input.firstName = this._firstName;
    }
    if (this._lastName) {
      input.lastName = this._lastName;
    }
    if (this._metadata) {
      input.metadata = this._metadata;
    }
    return input;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
